import { extractErrorMessage } from 'http/services/api.service'
import authService from 'http/services/auth.service'
import tokenService from 'http/services/token.service'
import { User } from 'domain/user'
import { LoginResponse } from 'features/sign-in/types/login-response'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useTransition,
  useCallback,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useLoading } from './loading-context'
import { ApiResponse } from 'http/types/api-response'

interface AuthTokens {
  accessToken: string
  refreshToken?: string
}

interface AuthContextType {
  user: User | null
  tokens: AuthTokens | null
  clientId: string | null
  isAuthenticated: boolean
  isInitialized: boolean
  isLoading: boolean
  isPending: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [tokens, setTokens] = useState<AuthTokens | null>(null)
  const [clientId, setClientId] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const navigate = useNavigate()
  const { isLoading, startLoading, stopLoading } = useLoading()
  const [isPending, startTransition] = useTransition()

  useEffect(() => {
    startLoading('AuthInit')

    const initializeAuth = async () => {
      // Skip during SSR
      if (typeof window === 'undefined') {
        stopLoading('AuthInit')
        setIsInitialized(true)
        return
      }

      try {
        const storedTokens = localStorage.getItem('auth_tokens')

        if (!storedTokens) {
          return
        }

        const parsedTokens: AuthTokens = JSON.parse(storedTokens)

        const userData = tokenService.getUserData()
        const clientIdFromToken = tokenService.getClientId()

        // Validate we have all required data
        if (!userData || !clientIdFromToken) {
          localStorage.removeItem('auth_tokens')
          localStorage.removeItem('user_data')
          return
        }

        // Restore auth state
        setTokens(parsedTokens)
        setUser(userData)
        setClientId(clientIdFromToken)
      } catch (error) {
        // Clear corrupted data
        localStorage.removeItem('auth_tokens')
        localStorage.removeItem('user_data')
        setUser(null)
        setTokens(null)
        setClientId(null)
      } finally {
        stopLoading('AuthInit')
        setIsInitialized(true)
      }
    }

    initializeAuth()
  }, [startLoading, stopLoading])

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      startLoading('Login')

      try {
        // 1. Authenticate with API
        const response: ApiResponse<LoginResponse> = await authService.login({
          username: email,
          password: password,
        })

        const { responseObject } = response

        // 2. Validate response
        if (!responseObject?.user?.clientId) {
          throw new Error('Invalid login response: missing user data')
        }

        // 4. Update state FIRST (so Protected Routes see the auth immediately)
        setTokens({
          accessToken: responseObject.accessToken,
          refreshToken: responseObject.refreshToken,
        })
        setUser(responseObject.user)
        setClientId(responseObject.user.clientId)

        // 5. Set the data to localStorage
        authService.storeLoginData(responseObject)

        // 6. Navigate using transition for smooth UX
        // Using startTransition keeps the UI responsive during navigation
        startTransition(() => {
          navigate('/', { replace: true })
        })

        // 7. Stop loading AFTER everything is complete
        // Dashboard will have access to auth state immediately
        stopLoading('Login')
      } catch (error: any) {
        // Ensure loading is stopped on error
        stopLoading('Login')

        // Clean up any partial state
        setUser(null)
        setTokens(null)
        setClientId(null)

        // Extract and re-throw error for UI to handle
        const errorMessage = extractErrorMessage(error)
        throw new Error(errorMessage)
      }
    },
    []
  )

  const logout = useCallback(() => {
    try {
      // 1. Clear localStorage first (critical - prevents auto-restore)
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('user_data')

      // 2. Stop any ongoing loading states
      stopLoading('Login')

      // 3. Clear state and navigate using transition
      startTransition(() => {
        setUser(null)
        setTokens(null)
        setClientId(null)
        navigate('/signin', { replace: true })
      })
    } catch (error) {
      setUser(null)
      setTokens(null)
      setClientId(null)

      // Force navigation
      window.location.href = '/signin'
    }
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        tokens,
        clientId,
        isAuthenticated: !!user && !!clientId, // Both must be present
        isInitialized,
        isLoading: isLoading || isPending,
        isPending,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
